import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getBrandPrefix } from "../lib/brand";
import {
  LogOut,
  LayoutDashboard,
  ListChecks,
  PackagePlus,
  Search,
  Bolt,
  HardHat,
  CheckCheck,
  Inbox,
  Boxes,
  ShieldCheck,
  Menu as MenuIcon,
  Trash2,
  Settings2,
  BarChart3,
  Wrench,
  BookOpen,
  KeyRound,
  FileText,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import NotificationBell from "./NotificationBell";
import ChangePasswordModal from "./ChangePasswordModal";
import LocationSwitcher from "./LocationSwitcher";

export default function Header() {
  const { user, logout, isPrivileged, role, userLocation, isGlobalAdmin } = useAuth();
  const brandPrefix = getBrandPrefix(user);
  const isRunnerLevel = isPrivileged || role === "runner";
  const nav = useNavigate();
  const loc = useLocation();
  const [pwOpen, setPwOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const roleBadge =
    user && typeof user === "object"
      ? user.role_label ||
        (user.role === "admin"
          ? "Admin"
          : user.role === "supervisor"
          ? "Supervisor"
          : user.role === "runner"
          ? "Yard Runner"
          : user.role === "crew_leader"
          ? "Crew Leader"
          : "Regular Crew")
      : "";

  const isActive = (to) =>
    to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);

  // Items inside the dropdown menu (everything besides the two pinned shortcuts)
  const menuItems = [
    { to: "/", label: "Home (Dashboard)", icon: LayoutDashboard, testid: "menu-dashboard", show: true },
    { to: "/shipments/receive", label: "Receive Shipment", icon: PackagePlus, testid: "menu-receive", show: true },
    { to: "/preparation-queue", label: "Yard Runner Queue", icon: ListChecks, testid: "menu-prep-queue", show: true },
    { to: "/my-requests", label: "My Requests", icon: Bolt, testid: "menu-my-requests", show: true },
    { to: "/my-pickups", label: "My Pending Pickups", icon: Inbox, testid: "menu-my-pickups", show: true },
    { to: "/inventory", label: "Low Inventory", icon: Boxes, testid: "menu-inventory", show: true },
    { to: "/equipment", label: "Equipment & Tools", icon: Wrench, testid: "menu-equipment", show: true },
    { to: "/tool-requests", label: "Tool Requests", icon: FileText, testid: "menu-tool-requests", show: true },
    { to: "/completed-tasks", label: "Completed Tasks", icon: CheckCheck, testid: "menu-completed", show: true },
    { to: "/reports", label: "Reports", icon: BarChart3, testid: "menu-reports", show: isRunnerLevel },
    { to: "/shipments", label: "Search Shipments", icon: Search, testid: "menu-find", show: true },
    { to: "/help", label: "User Guide", icon: BookOpen, testid: "menu-help", show: true },
  ].filter((m) => m.show);

  const adminMenuItems = [
    { to: "/admin/roles", label: "User Roles", icon: ShieldCheck, testid: "menu-admin-roles" },
    { to: "/admin/dropdowns", label: "Manage Dropdowns", icon: Settings2, testid: "menu-admin-dropdowns" },
    { to: "/admin/tool-catalog", label: "ComEd Tool Catalog", icon: Wrench, testid: "menu-admin-tool-catalog" },
    { to: "/crew-leaders", label: "Crew Leaders", icon: HardHat, testid: "menu-admin-crew-leaders" },
    { to: "/admin/deletions", label: "Recent Deletions", icon: Trash2, testid: "menu-admin-deletions" },
  ];

  return (
    <header
      className="sticky top-0 z-50 bg-slate-900 text-white border-b-4 border-amber-500"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0" data-testid="logo-link">
          <img
            src="/powermove-mark.png"
            alt={brandPrefix}
            className="h-9 w-9 rounded-sm object-contain bg-slate-950 border border-slate-800"
          />
          <div className="leading-none hidden xs:block sm:block">
            <div className="font-display text-base sm:text-lg uppercase tracking-wider" data-testid="header-brand-prefix">
              {brandPrefix}
            </div>
            <div className="text-[10px] sm:text-xs text-slate-300 uppercase tracking-[0.18em] hidden sm:block">
              Material Tracker
            </div>
          </div>
        </Link>

        {/* Two permanent shortcut buttons + Menu dropdown */}
        <nav className="flex items-center gap-1.5 sm:gap-2 flex-1 justify-end min-w-0">
          {/* Receive — always visible, prominent green */}
          <Link
            to="/shipments/receive"
            data-testid="nav-receive"
            aria-label="Receive Shipment"
            className={`rg-tap px-2.5 sm:px-3 py-2 rounded-sm text-xs sm:text-sm font-bold uppercase tracking-wide flex items-center gap-1.5 sm:gap-2 border-2 transition-colors flex-shrink-0 ${
              isActive("/shipments/receive")
                ? "bg-green-800 text-white border-green-950"
                : "bg-green-700 text-white hover:bg-green-800 border-green-900"
            }`}
          >
            <PackagePlus strokeWidth={3} className="h-4 w-4" />
            <span>Receive</span>
          </Link>

          {/* Runner Queue — always visible */}
          <Link
            to="/preparation-queue"
            data-testid="nav-prep-queue"
            aria-label="Yard Runner Queue"
            className={`rg-tap px-2.5 sm:px-3 py-2 rounded-sm text-xs sm:text-sm font-bold uppercase tracking-wide flex items-center gap-1.5 sm:gap-2 border-2 transition-colors flex-shrink-0 ${
              isActive("/preparation-queue")
                ? "bg-amber-500 text-slate-900 border-amber-600"
                : "bg-transparent text-slate-200 hover:bg-slate-800 border-slate-700 hover:border-amber-500"
            }`}
          >
            <ListChecks strokeWidth={2.5} className="h-4 w-4" />
            <span className="hidden sm:inline">Runner Queue</span>
            <span className="sm:hidden">Queue</span>
          </Link>

          {/* Notifications */}
          {user && user !== false && <NotificationBell />}

          {/* Location switcher — global admins only. Auto-injects ?location=
              on every GET via the axios interceptor. */}
          {user && user !== false && (
            <LocationSwitcher
              isGlobalAdmin={isGlobalAdmin}
              currentUserLocation={userLocation}
            />
          )}

          {/* Menu dropdown — everything else */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="header-menu-trigger"
                aria-label="Open menu"
                className="rg-tap h-10 px-2.5 sm:px-3 border-2 border-slate-700 hover:border-amber-500 hover:bg-slate-800 rounded-sm flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold uppercase tracking-wide text-slate-200 flex-shrink-0"
              >
                <MenuIcon strokeWidth={2.5} className="h-5 w-5" />
                <span className="hidden sm:inline">Menu</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={6}
              className="w-72 sm:w-80"
              data-testid="header-menu-content"
            >
              {/* User block */}
              {user && user !== false && (
                <>
                  <div className="px-3 py-2.5 border-b-2 border-slate-200">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 flex items-center justify-between">
                      <span>Signed in</span>
                      {roleBadge && (
                        <span
                          className="bg-amber-500 text-slate-900 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm"
                          data-testid="header-role-badge"
                        >
                          {roleBadge}
                        </span>
                      )}
                    </div>
                    <div
                      className="text-sm font-bold text-slate-900 mt-0.5 truncate"
                      data-testid="header-user-name"
                    >
                      {user.name || user.email}
                    </div>
                    {(userLocation || isGlobalAdmin) && (
                      <div
                        className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600 flex items-center gap-1.5"
                        data-testid="header-user-location"
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        <span>Location: {userLocation || "—"}</span>
                        {isGlobalAdmin && (
                          <span className="ml-1 bg-rose-600 text-white px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded-sm">
                            Global
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Main menu
              </DropdownMenuLabel>
              {menuItems.map(({ to, label, icon: Icon, testid }) => (
                <DropdownMenuItem
                  key={to}
                  asChild
                  className={`min-h-[44px] cursor-pointer text-sm font-bold ${
                    isActive(to) ? "bg-amber-50 text-slate-900" : ""
                  }`}
                >
                  <Link to={to} data-testid={testid} className="flex items-center gap-2.5 w-full">
                    <Icon strokeWidth={2.5} className="h-4 w-4 flex-shrink-0 text-slate-600" />
                    <span>{label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}

              {isPrivileged && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-rose-700">
                    Admin tools
                  </DropdownMenuLabel>
                  {adminMenuItems.map(({ to, label, icon: Icon, testid }) => (
                    <DropdownMenuItem
                      key={to}
                      asChild
                      className={`min-h-[44px] cursor-pointer text-sm font-bold ${
                        isActive(to) ? "bg-rose-50 text-rose-900" : ""
                      }`}
                    >
                      <Link to={to} data-testid={testid} className="flex items-center gap-2.5 w-full">
                        <Icon strokeWidth={2.5} className="h-4 w-4 flex-shrink-0 text-rose-700" />
                        <span>{label}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setPwOpen(true)}
                data-testid="menu-change-password"
                className="min-h-[44px] cursor-pointer text-sm font-bold"
              >
                <KeyRound strokeWidth={2.5} className="h-4 w-4 mr-2 text-slate-600" />
                Change password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onLogout}
                data-testid="menu-logout"
                className="min-h-[44px] cursor-pointer text-sm font-bold text-rose-700 focus:text-rose-700 focus:bg-rose-50"
              >
                <LogOut strokeWidth={2.5} className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </header>
  );
}
